import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { CheckCircle2, ExternalLink, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * NexFlow Plumbing — Twilio SMS Compliance Screenshot Package.
 *
 * This is a public, no-auth page designed specifically for Twilio Toll-Free
 * Verification reviewers. It hosts the actual consent flow screenshots, the
 * verbatim consent text, the data we capture, our STOP/HELP behavior, and
 * the brand identity. Linking this single page in a Twilio resubmission gives
 * the reviewer everything they need in one place.
 */
const SHOTS = [
  {
    id: '01-sms-consent-page',
    title: '1. Dedicated SMS consent page (/sms-consent)',
    caption: 'Standalone consent page. SMS consent is presented separately from Terms of Service and Privacy Policy. The checkbox is unchecked by default and uses the verbatim consent language required by carriers.',
    href: '/sms-consent',
  },
  {
    id: '02-sms-consent-checked',
    title: '2. Verbatim consent checkbox — checked state',
    caption: 'The user actively ticks the consent box. The full verbatim consent paragraph including STOP / HELP keywords and the "Consent is not a condition of purchase" disclosure is in the same label as the checkbox.',
    href: '/sms-consent',
  },
  {
    id: '03-sms-consent-success',
    title: '3. Confirmation after submission',
    caption: 'On successful submission we store consent version, date, time (UTC), IP address, user-agent, full name, email, and E.164 phone number. The confirmation displays the recorded version and timestamp back to the user.',
    href: '/sms-consent',
  },
  {
    id: '04-register-form',
    title: '4. Registration form with SEPARATE SMS consent',
    caption: 'New-account registration. The SMS consent checkbox is OPTIONAL, unchecked by default, and appears as a distinct field below the password field. It is never bundled with Terms of Service acceptance.',
    href: '/register',
  },
  {
    id: '05-terms',
    title: '5. Terms of Service — no SMS bundling',
    caption: 'Our Terms of Service explicitly state that SMS consent is collected separately at /sms-consent and is never granted by accepting the Terms.',
    href: '/terms',
  },
  {
    id: '06-privacy',
    title: '6. Privacy Policy — SMS data handling',
    caption: 'Our Privacy Policy explains exactly which SMS-related fields we store, why, and confirms we do not share or sell SMS data to third parties.',
    href: '/privacy',
  },
];

const FieldRow = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-[#262626] last:border-0 py-2 text-sm">
    <span className="text-[#A3A3A3]">{k}</span>
    <span className="text-white font-mono text-xs text-right break-all">{v}</span>
  </div>
);

const SmsCompliance = () => {
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/sms-consent-text`).then(({ data }) => setPolicy(data)).catch(() => {});
  }, []);

  const consentUrl = typeof window !== 'undefined' ? `${window.location.origin}/sms-consent` : '/sms-consent';

  return (
    <div data-testid="sms-compliance-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="border-b border-[#262626]">
        <div className="max-w-5xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><Logo size="sm" /><span className="font-['Outfit'] font-semibold">NEXFLOW PLUMBING &amp; DRAIN SERVICES</span></Link>
          <Link to="/" className="text-[#A3A3A3] text-sm hover:text-white">Home</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-10 space-y-10">
        {/* Hero */}
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-2">TWILIO COMPLIANCE</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold mb-3">SMS Compliance Screenshot Package</h1>
          <p className="text-[#A3A3A3] text-base leading-relaxed max-w-3xl">
            This page is built for Twilio Toll-Free Verification reviewers. It documents how NexFlow Plumbing &amp;
            Drain Services collects SMS consent, what we store, the verbatim consent language, and our STOP / HELP
            keyword behavior. Everything below is reproducible from a live URL.
          </p>
        </div>

        {/* Brand summary */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">BRAND</p>
            <FieldRow k="Legal Name" v="NexFlow Inc." />
            <FieldRow k="DBA" v="NexFlow Plumbing &amp; Drain Services" />
            <FieldRow k="Industry" v="Plumbing &amp; drain services" />
            <FieldRow k="Country" v="United States" />
            <FieldRow k="Use case" v="Customer Care / Account Notification" />
            <FieldRow k="Production URL" v="https://hub.nexflowplumbing.com" />
            <FieldRow k="Consent URL" v={consentUrl} />
          </div>
          <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">FIELDS STORED PER CONSENT</p>
            <ul className="text-[#A3A3A3] text-sm space-y-1.5">
              <li><span className="text-white">full_name</span> — submitted on the form</li>
              <li><span className="text-white">email</span> — lowercased</li>
              <li><span className="text-white">phone</span> — normalized to E.164</li>
              <li><span className="text-white">consent_version</span> — e.g. <span className="font-mono">{policy?.version || 'v2-2026-06-05'}</span></li>
              <li><span className="text-white">consent_date</span> — UTC yyyy-mm-dd</li>
              <li><span className="text-white">consent_time</span> — UTC HH:MM:SS</li>
              <li><span className="text-white">consent_at</span> — ISO 8601 datetime</li>
              <li><span className="text-white">ip</span> — first hop of <span className="font-mono">x-forwarded-for</span></li>
              <li><span className="text-white">user_agent</span> — full UA string</li>
              <li><span className="text-white">consent_source</span> — <span className="font-mono">sms_consent_page</span> or <span className="font-mono">registration</span></li>
            </ul>
          </div>
        </div>

        {/* Verbatim text */}
        <div className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-6 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">VERBATIM CONSENT LANGUAGE</p>
          <p data-testid="compliance-verbatim-text" className="text-white text-base leading-relaxed">
            {policy?.text || 'Loading…'}
          </p>
          <p className="text-[#525252] text-xs">
            Active version: <span className="text-[#D4AF37] font-mono">{policy?.version || 'v2-2026-06-05'}</span> ·
            Fetched live from <span className="font-mono">GET /api/public/sms-consent-text</span>
          </p>
        </div>

        {/* Screenshots gallery */}
        <div className="space-y-6">
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-2">SCREENSHOT PACKAGE</p>
            <p className="text-[#A3A3A3] text-sm">
              Each image below is reproducible from the linked URL. Click <span className="text-[#D4AF37]">View live page</span> to verify the screenshot was not edited.
            </p>
          </div>
          {SHOTS.map((s) => (
            <div key={s.id} data-testid={`compliance-shot-${s.id}`} className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-[#262626] flex flex-wrap items-center justify-between gap-3">
                <p className="text-white font-medium">{s.title}</p>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#D4AF37] text-xs font-medium hover:text-[#F3E5AB] inline-flex items-center gap-1"
                >
                  View live page <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-[#A3A3A3] text-sm">{s.caption}</p>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded">
                  <img
                    src={`/sms-compliance/${s.id}.png`}
                    alt={s.title}
                    className="w-full h-auto block"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
                  />
                  <div style={{ display: 'none' }} className="p-6 text-center text-[#525252] text-xs">
                    Screenshot pending — open <a href={s.href} className="text-[#D4AF37] underline" target="_blank" rel="noreferrer">{s.href}</a> in a new tab to view the live page.
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* STOP / HELP behavior */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">STOP / HELP KEYWORD BEHAVIOR</p>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-white font-medium mb-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> STOP</p>
              <p className="text-[#A3A3A3]">When a user replies STOP, all outbound SMS to that number ceases immediately. We honor STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, and QUIT. A single confirmation message is sent: <span className="text-white">&ldquo;You have been unsubscribed from NexFlow Plumbing SMS messages. Reply START to re-subscribe.&rdquo;</span></p>
            </div>
            <div>
              <p className="text-white font-medium mb-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> HELP</p>
              <p className="text-[#A3A3A3]">Replying HELP triggers: <span className="text-white">&ldquo;NexFlow Plumbing &amp; Drain Services — for assistance call (614) 555-1212 or email support@nexflowplumbing.com. Msg &amp; data rates may apply. Reply STOP to opt out.&rdquo;</span></p>
            </div>
          </div>
        </div>

        {/* Contact for reviewers */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">REVIEWER CONTACT</p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <p className="text-[#A3A3A3] flex items-center gap-2"><Mail className="w-4 h-4 text-[#D4AF37]" /> compliance@nexflowplumbing.com</p>
            <p className="text-[#A3A3A3] flex items-center gap-2"><Phone className="w-4 h-4 text-[#D4AF37]" /> (614) 555-1212</p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#262626] pt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" className="text-[#A3A3A3] hover:text-white">Terms of Service</Link>
          <Link to="/privacy" className="text-[#A3A3A3] hover:text-white">Privacy Policy</Link>
          <Link to="/sms-consent" className="text-[#A3A3A3] hover:text-white">SMS Consent</Link>
          <Link to="/sms-compliance" className="text-[#A3A3A3] hover:text-white">SMS Compliance</Link>
        </div>
      </div>
    </div>
  );
};

export default SmsCompliance;
